import { useCallback, useRef } from 'react'
import HttpStatus from 'http-status-codes'
import firebase from 'firebase/compat/app'
import * as Sentry from '@sentry/react'

import { playerStoreInitialValues } from '../../services/store/playerStore/playerStoreConstants'
import { config } from '../../components/Init/Init'
import { tokenizer } from '../../services/utils'
import l10n from '../../components/i18n/I18N'
import { usePlayerStore } from '../../services/store'
import { useStateValue } from '../../services/state/StateProvider'
import { useSetStoreDefaultData } from '../../services/store/storeActions'
import { isZendeskWidgetAdded, logoutZendeskUser } from '../../components/Help/ZendeskWidget'

export const useLogoutPlayer = () => {
  const [_, dispatch] = useStateValue()

  const playerStore = usePlayerStore()
  const playerAccessToken = playerStore.use.accessToken()
  const playerLoginUser = playerStore.use.loginUser()
  const playerActiveTabId = playerStore.use.activeTabId()

  const { setStoreDefaultData } = useSetStoreDefaultData()

  const currentControllerRef = useRef(null)

  const resetStores = useCallback(
    logoutPlayerData => {
      // set default value to all sliced zustand store
      // note: cause resetting of localStorage
      setStoreDefaultData({
        allowAutomaticLogin: logoutPlayerData.allowAutomaticLogin ?? playerStoreInitialValues.allowAutomaticLogin,
        activeTabId: playerActiveTabId ?? playerStoreInitialValues.activeTabId
      })

      // reset all non zustand data
      // remove after full migration
      dispatch({
        type: 'resetAllContextData',
        general: {}
      })
    },
    [dispatch, playerActiveTabId, setStoreDefaultData]
  )

  const deleteFirebaseConnection = useCallback(() => {
    // TODO: take rmgBase out of window
    // remove firebase
    try {
      firebase
        .app()
        .delete()
        .then(() => {
          window.rmgBase = undefined
        })
        .catch(e => console.log(['[logout] - Error deleting default Firebase app. ', e]))

      firebase
        .app('casinoDB')
        .delete()
        .then(() => {
          window.casinoDB = undefined
        })
        .catch(e => console.log(['[logout] - Error deleting casinoDB Firebase app. ', e]))
    } catch (e) {
      console.log(['[logout] - Error deleting casinoDB Firebase app. ', e])
    }
  }, [])

  const logoutPlayer = useCallback(
    logoutPlayerData => {
      if (isZendeskWidgetAdded()) {
        logoutZendeskUser()
      }

      if (window.rmgBaseSubscriptions) {
        if (window.rmgBase) {
          const db = window.rmgBase.database()
          window.rmgBaseSubscriptions.forEach(subscriberPath => db.ref(subscriberPath).off())
        }

        window.rmgBaseSubscriptions = []
        window.sessionEventInitialResponseCalled = false
        window.sessionKickedInitialResponseCalled = false
      }

      if (window.messagingDBSubscriptions && window.rmgBase) {
        let messagingDB = config.firebaseUseMessagingDb
          ? window.rmgBase.database(config.messagingFirebaseConfig.databaseURL)
          : window.rmgBase.database()
        window.messagingDBSubscriptions.forEach(subscriberPath => messagingDB.ref(subscriberPath).off())
        window.messagingDBSubscriptions = []
      }

      if (window.casinoDBSubscriptions && window.casinoDB) {
        let casinoDB = window.casinoDB.database()
        window.casinoDBSubscriptions.forEach(subscriberPath => casinoDB.ref(subscriberPath).off())
        window.casinoDBSubscriptions = []
      }

      if (!config.allowTabDuplication) {
        sessionStorage.removeItem('activeTabId')
      }

      if (window.rmgBase) {
        window.rmgBase
          .auth()
          .signOut()
          .then(() => {
            // if refresh is requested there's no need to return an anonymous
            // (logged out) player
            logoutFromServer({
              refresh: !!logoutPlayerData.refresh
            })
          })
          .catch(err => {
            console.log('[logout] ERROR! Signing out of Firebase', err)
          })
      }

      const logoutFromServer = ({ refresh }) => {
        if (playerLoginUser.userId === undefined) {
          resetStores(logoutPlayerData)

          if (refresh) {
            deleteFirebaseConnection()
          }

          return
        }

        if (currentControllerRef.current) {
          currentControllerRef.current.abort()
        }

        currentControllerRef.current = new AbortController()

        try {
          fetch(
            config.apiUrl +
              tokenizer(config.logoutUrl, {
                '{userId}': playerLoginUser.userId
              }),
            {
              method: 'DELETE',
              signal: currentControllerRef.current.signal,
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${playerAccessToken.encryptedToken}`
              }
            }
          )
            .then(response => {
              if (response.status === HttpStatus.OK) {
                resetStores(logoutPlayerData)

                if (refresh) {
                  deleteFirebaseConnection()
                }
              }

              currentControllerRef.current = null
            })
            .catch(err => {
              console.log('[logout] ERROR!', err)

              resetStores(logoutPlayerData)

              if (refresh) {
                deleteFirebaseConnection()
              }

              currentControllerRef.current = null
            })
        } catch (e) {
          console.log('[player actions] error:', e)
        }
      }

      // if we're using sentry, update sentry user
      if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
        Sentry.setUser({})
      }

      l10n.setLocale({
        isoLocale: `${playerStoreInitialValues.loginUser.preferredLanguageCode}-${playerStoreInitialValues.country}`,
        locales: config.locales,
        isLoggedIn: false,
        generalLanguages: config.generalLanguages
      })
    },
    [deleteFirebaseConnection, playerAccessToken.encryptedToken, playerLoginUser.userId, resetStores]
  )

  return { logoutPlayer }
}
